import React, { useState, useEffect, useRef } from "react";
import { Calendar } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Input } from "./../Components/ui/input";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "./../Components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "./../Components/ui/button";

export const DatePicker = ({
  date,
  onChange,
  color,
  minDate,
  maxDate,
  disabled,
}) => {
  const handleDateChange = (newDate) => {
    onChange(newDate);
  };

  return (
    <Calendar
      date={date}
      disabled={disabled}
      onChange={handleDateChange}
      color={color}
      minDate={minDate}
      maxDate={maxDate}
    />
  );
};

const TransactionDatePicker = ({
  id = "DatePicker",
  value,
  changeDate = () => {},
  errorMessage,
  name = "dob",
  className,
  disabled,
  require18Years = true,
  onRawInputChange,
}) => {
  const [displayCalendar, setDisplayCalendar] = useState(false);
  const [inputTxt, setInputTxt] = useState("");
  const [error, setError] = useState(errorMessage);
  const calendarRef = useRef(null);
  const [isDirty, setIsDirty] = useState(false);
  useEffect(() => {
    setError(errorMessage);
  }, [errorMessage]);

  const formatInputValue = (value) => {
    setError("");
    // Remove all non-digits
    const cleaned = value?.replace(/\D/g, "");

    // Format as DD-MM-YYYY
    if (cleaned.length <= 2) {
      return cleaned;
    } else if (cleaned.length <= 4) {
      const month = cleaned?.slice(2);
      if (month > 12) {
        setError(validateInput(value));
      }
      return `${cleaned?.slice(0, 2)}-${cleaned?.slice(2)}`;
    } else {
      const month = cleaned?.slice(2, 4);
      // Ensure month is not greater than 12
      const validMonth =
        month.length === 2 && parseInt(month) > 12 ? "12" : month;

      return `${cleaned?.slice(0, 2)}-${validMonth}-${cleaned?.slice(4, 8)}`;
    }
  };

  const dateFormatter = (mydate) => {
    if (!mydate) {
      return "";
    }
    let curr_date = mydate.getDate();
    let curr_month = mydate.getMonth() + 1;
    let curr_year = mydate.getFullYear();

    if (curr_date < 10) {
      curr_date = "0" + curr_date;
    }

    if (curr_month < 10) {
      curr_month = "0" + curr_month;
    }

    return `${curr_date}-${curr_month}-${curr_year}`;
  };

  const parseDateString = (dateStr) => {
    const parts = dateStr?.split("-");
    if (parts.length !== 3) return null;

    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    // Strict validation for month
    if (month < 1 || month > 12) return null;

    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

    const date = new Date(year, month - 1, day);

    // Additional validation to ensure the date is valid
    if (
      date.getDate() !== day ||
      date.getMonth() !== month - 1 ||
      date.getFullYear() !== year
    ) {
      return null;
    }

    return date;
  };

  const validateDate = (date) => {
    if (require18Years) {
      const today = new Date();
      const eighteenYearsAgo = new Date(
        today.setFullYear(today.getFullYear() - 18)
      );

      if (date > eighteenYearsAgo) {
        return "You must be at least 18 years old.";
      }
    }

    const minDate = new Date("1900-01-01");
    const maxDate = require18Years
      ? new Date(new Date().setFullYear(new Date().getFullYear() - 18))
      : new Date();

    if (date < minDate) {
      return "Date cannot be earlier than 01-01-1900.";
    }
    if (date > maxDate) {
      return require18Years
        ? "You must be at least 18 years old."
        : "Date cannot be in the future.";
    }

    return "";
  };

  const validateInput = (value) => {
    if (!value.trim()) {
      return "";
    }

    // Only validate complete dates
    if (value.length < 10) {
      return "Please enter date in DD-MM-YYYY format";
    }

    if (!/^\d{2}-\d{2}-\d{4}$/.test(value)) {
      return "Please enter date in DD-MM-YYYY format";
    }

    const parsedDate = parseDateString(value);
    if (!parsedDate) {
      return "Please enter a valid date";
    }

    return validateDate(parsedDate);
  };

  const calendarMaxDate = require18Years
    ? new Date(new Date().setFullYear(new Date().getFullYear() - 18))
    : new Date();
  const calendarMinDate = new Date("1900-01-01");

  const showCalendar = (e) => {
    e.stopPropagation();
    setDisplayCalendar((prevState) => !prevState);
  };

  const hideCalendar = () => {
    setDisplayCalendar(false);
  };

  const handleInputChange = (e) => {
    const rawValue = e.target.value;

    // Only allow digits and hyphens
    if (!/^[\d-]*$/.test(rawValue)) {
      return;
    }

    // Limit the total length to 10 characters (DD-MM-YYYY)
    if (rawValue.replace(/\D/g, "").length > 8) {
      return;
    }

    const formattedValue = formatInputValue(rawValue);
    setInputTxt(formattedValue);
    setIsDirty(true);

    // Pass the raw input to parent component if prop exists
    if (onRawInputChange) {
      onRawInputChange({
        target: {
          name: name,
          value: formattedValue,
        },
      });
    }
    // Clear value if input is empty
    if (!formattedValue.trim()) {
      const syntheticEvent = {
        target: {
          name: name,
          value: null,
        },
      };
      changeDate(syntheticEvent);
    }
  };

  const handleBlur = () => {
    if (!isDirty) return;

    const validationError = validateInput(inputTxt);
    setError(validationError);

    // If there's no validation error and the input is complete
    if (!validationError && inputTxt.length === 10) {
      const parsedDate = parseDateString(inputTxt);
      const syntheticEvent = {
        target: {
          name: name,
          value: parsedDate,
        },
      };
      changeDate(syntheticEvent);
    }
  };

  const handleSelect = (date) => {
    const validationError = validateDate(date);
    if (validationError) {
      setError(validationError);
      return;
    }

    setError("");
    const syntheticEvent = {
      target: {
        name: name,
        value: date,
        isValid: true,
      },
    };

    changeDate(syntheticEvent);
    const formattedDate = dateFormatter(date);
    setInputTxt(formattedDate);

    // Also notify about raw input change
    if (onRawInputChange) {
      onRawInputChange({
        target: {
          name: name,
          value: formattedDate,
        },
      });
    }

    setIsDirty(true);
    hideCalendar();
  };

  const handleDocumentClick = (e) => {
    if (calendarRef.current && !calendarRef.current.contains(e.target)) {
      hideCalendar();
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleDocumentClick);
    return () => {
      document.removeEventListener("click", handleDocumentClick);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (value) {
      const formattedValue =
        value instanceof Date
          ? dateFormatter(value)
          : typeof value === "string"
          ? value
          : "";
      setInputTxt(formattedValue);
      setError("");
      setIsDirty(false);
    }
  }, [value]);

  return (
    <div className="relative">
      <div className="flex flex-col items-start w-full">
        <Popover open={displayCalendar} onOpenChange={setDisplayCalendar}>
          <div className="relative w-full">
            <Input
              type="text"
              name={name}
              value={inputTxt}
              disabled={disabled}
              onChange={handleInputChange}
              onBlur={handleBlur}
              placeholder="DD-MM-YYYY"
              className={`px-1 py-3 pr-10 ${className} ${
                error ? "border-red-500" : ""
              }`}
            />
            <PopoverTrigger asChild>
              <button
                type="button"
                onClick={showCalendar}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-md"
              >
                <CalendarIcon className="h-5 w-5 text-gray-500" />
              </button>
            </PopoverTrigger>
          </div>
          <PopoverContent
            className="z-[1000]"
            align="start"
            side="bottom"
            sideOffset={4}
            ref={calendarRef}
          >
            <DatePicker
              disabled={disabled}
              onChange={handleSelect}
              maxDate={calendarMaxDate}
              minDate={calendarMinDate}
              date={value instanceof Date ? value : calendarMaxDate}
              color="#0061D3"
            />
          </PopoverContent>
        </Popover>
        {error && <p className="text-failed text-sm mt-1 ml-2">{error}</p>}
      </div>
    </div>
  );
};

const CustomDatePicker = ({
  date,
  onChange,
  color,
  minDate,
  maxDate,
  text = "From",
  displayCalendar,
  setDisplayCalendar,
}) => {
  const calendarRef = useRef(null);
  const formattedDate = date ? new Date(date) : undefined;
  const formattedMinDate = minDate ? new Date(minDate) : undefined;
  const formattedMaxDate = maxDate ? new Date(maxDate) : undefined;

  return (
    <Popover open={displayCalendar} onOpenChange={setDisplayCalendar}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className=" justify-start text-left font-normal bg-white border"
        >
          <div className="flex items-center gap-2">
            <span className="text-gray-500">{text}</span>
            {date ? (
              <span className=" font-geomanistBold text-[14px] leading-[20px] text-blackShade">
                {" "}
                {date}
                {/* {formattedDate.toLocaleDateString()} */}
              </span>
            ) : (
              <span className="text-gray-500">Pick a date</span>
            )}
            <CalendarIcon className="ml-auto h-4 w-4 text-gray-500" />
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        side="bottom"
        sideOffset={4}
        ref={calendarRef}
      >
        <DatePicker
          date={formattedDate}
          onChange={onChange}
          color={color}
          minDate={formattedMinDate}
          maxDate={formattedMaxDate}
        />
      </PopoverContent>
    </Popover>
  );
};

export { CustomDatePicker, TransactionDatePicker };
